import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { ApiError } from "@/core/api/types";
import {
  followAiScriptExplanationChatTurn,
  runAiScriptExplanationChatTurn,
} from "@/payroll/ai-script/explanation-chat-transport";
import type {
  ExplanationChatPathBuilder,
  IAiScriptExplanationChatThread,
  IAiScriptExplanationChatThreadResponse,
  IAiScriptExplanationChatThreadsResponse,
} from "@/payroll/ai-script/types";
import { getAiScriptExplanationChatPath } from "./paths";

interface CreateAiScriptExplanationChatParams {
  runId: string;
  message: string;
  threadId?: string | null;
  threadName?: string | null;
  images?: File[];
  onThread?: (thread: IAiScriptExplanationChatThread, turnId: string) => void;
  onTextDelta?: (delta: string) => void;
  onProgress?: (label: string) => void;
}

interface FollowAiScriptExplanationChatParams {
  runId: string;
  threadId: string;
  turnId: string;
  onTextDelta?: (delta: string) => void;
  onProgress?: (label: string) => void;
}

const AI_SCRIPT_EXPLANATION_CHAT_THREADS_KEY =
  "ai-script-explanation-chat-threads";

const AI_SCRIPT_EXPLANATION_CHAT_THREAD_KEY =
  "ai-script-explanation-chat-thread";

const chatRoot = (
  businessId: string | null,
  runId: string | null,
  buildPath: ExplanationChatPathBuilder,
) => (businessId && runId ? buildPath(businessId, runId) : null);

export const aiScriptExplanationChatThreadsKey = (
  businessId: string | null,
  runId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) =>
  [
    AI_SCRIPT_EXPLANATION_CHAT_THREADS_KEY,
    chatRoot(businessId, runId, buildPath),
  ] as const;

export const isChatAccessDeniedError = (error: unknown) =>
  error instanceof ApiError && error.status === 403;

export const useGetAiScriptExplanationChatThreads = (
  businessId: string | null,
  runId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) => {
  const api = useApi();

  return useQuery({
    queryKey: aiScriptExplanationChatThreadsKey(businessId, runId, buildPath),
    queryFn: () => {
      if (!businessId || !runId) throw new Error("Payroll run is required");

      return api
        .get(`${buildPath(businessId, runId)}/threads`)
        .then((res) => res.data as IAiScriptExplanationChatThreadsResponse);
    },
    enabled: !!businessId && !!runId,
    retry: (failureCount, error) =>
      !isChatAccessDeniedError(error) && failureCount < 2,
  });
};

export const aiScriptExplanationChatThreadKey = (
  businessId: string | null,
  runId: string | null,
  threadId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) =>
  [
    AI_SCRIPT_EXPLANATION_CHAT_THREAD_KEY,
    chatRoot(businessId, runId, buildPath),
    threadId,
  ] as const;

const aiScriptExplanationChatThreadListKey = (
  businessId: string | null,
  runId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) =>
  [
    AI_SCRIPT_EXPLANATION_CHAT_THREAD_KEY,
    chatRoot(businessId, runId, buildPath),
  ] as const;

export const useGetAiScriptExplanationChatThread = (
  businessId: string | null,
  runId: string | null,
  threadId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) => {
  const api = useApi();

  return useQuery({
    queryKey: aiScriptExplanationChatThreadKey(
      businessId,
      runId,
      threadId,
      buildPath,
    ),
    queryFn: () => {
      if (!businessId || !runId || !threadId)
        throw new Error("Thread is required");

      return api
        .get(`${buildPath(businessId, runId)}/threads/${threadId}`)
        .then((res) => res.data as IAiScriptExplanationChatThreadResponse);
    },
    enabled: !!businessId && !!runId && !!threadId,
  });
};

export const useCreateAiScriptExplanationChat = (
  businessId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      runId,
      message,
      threadId,
      threadName,
      images,
      onThread,
      onTextDelta,
      onProgress,
    }: CreateAiScriptExplanationChatParams) => {
      if (!businessId) throw new Error("Business is required");

      return runAiScriptExplanationChatTurn({
        api,
        chatPath: buildPath(businessId, runId),
        message,
        threadId,
        threadName,
        images,
        onThread,
        onTextDelta,
        onProgress,
      });
    },
    onSettled: (_result, _error, { runId }) => {
      void queryClient.invalidateQueries({
        queryKey: aiScriptExplanationChatThreadsKey(
          businessId,
          runId,
          buildPath,
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: aiScriptExplanationChatThreadListKey(
          businessId,
          runId,
          buildPath,
        ),
      });
    },
  });
};

export const useFollowAiScriptExplanationChatTurn = (
  businessId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      runId,
      threadId,
      turnId,
      onTextDelta,
      onProgress,
    }: FollowAiScriptExplanationChatParams) => {
      if (!businessId) throw new Error("Business is required");

      const response = await followAiScriptExplanationChatTurn({
        api,
        chatPath: buildPath(businessId, runId),
        turnId,
        onTextDelta,
        onProgress,
      });

      return { response, threadId, turnId };
    },
    onSettled: (_result, _error, { runId }) => {
      void queryClient.invalidateQueries({
        queryKey: aiScriptExplanationChatThreadsKey(
          businessId,
          runId,
          buildPath,
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: aiScriptExplanationChatThreadListKey(
          businessId,
          runId,
          buildPath,
        ),
      });
    },
  });
};
